import Fetcher from "../fetcher";
import { type } from "../charge/utils";
import Supports from "../utils/supports";
import { falseFunction } from "./helpers";
import withReactiveFields from "../context/payment/helpers/reactivefields";
import { DEFAULT_POST_INIT, DEFAULT_RETRY_CONDITIONS, isJsonResponse, withSharedHeaders } from "../fetcher/utils";

import {
  isString,
  isFunction,
  isPlainObject,
  withProperties,
  getValueAtPath,
  objectWithAccessors,
  removeFromArray
} from "../utils";

const $fetcher = withSharedHeaders(Fetcher.one
  .limit(5)
  .timeout(6000)
  .init(DEFAULT_POST_INIT)
  .retry(DEFAULT_RETRY_CONDITIONS));

const __SavedCardProto__ = Object.freeze(
  withProperties(Object.create(null), {
    toString() {
      const mask = this.masked_pan;
      return isString(mask)
        ? mask.replace(/^[*\d]+?(\d{4})$/, (_, $1) => `**** ${$1}`)
        : mask;
    }
  })
);

const SAVED_CARDS = Object.create(null);

let SAVED_CARDS_LIST = [];

function checkSavedCardsEnabled(callback) {
  return (
    getValueAtPath(this, 'preferences[blacklist_saved_cards]') !== true &&
    (isFunction(callback) ? callback() : true)
  );
}

function fetchSavedCards() {
  const $this = this;
  let deviceKey = Supports.localStorage ? localStorage.getItem('device_mobile_key') : null;

  return Promise.resolve(
    checkSavedCardsEnabled.call(this, () => (
      Object.isFrozen(SAVED_CARDS) || ( 
        deviceKey !== null 
          ? $fetcher(new URL('/v2/gpx/users/lookup', process.env.API_BASE_URL), {
            body: JSON.stringify({
              modalauditid: getValueAtPath($this, 'modalauditid'),
              public_key: getValueAtPath($this, 'PBFPubKey'),
              // device_key: '+2348090727295'
              device_key: deviceKey
            })
          })
            .then(response => {
              if (isJsonResponse(response) && response.ok) {
                return response.json()
                  .then(response => {
                    let fetched = false;
                    let initialized = false;
                    const { data, status } = isPlainObject(response) ? response : {};
    
                    if (status === 'success' && type(data) === 'array') {
                      data.forEach(item => {
                        if (isPlainObject(item)) {
                          if (!initialized) {
                            const { device: id, mobile_number: phone, email } = item;
                            const device = withProperties(Object.create(null), { id, email, phone });
                            withProperties(SAVED_CARDS, { device: Object.freeze(device) });
                            initialized = true;
                          }
    
                          const { card, card_hash: hash } = item;
                          const { card_brand: brand, masked_pan } = isPlainObject(card) ? card : {};
                          const card6 = String(masked_pan).substring(0, 6);
    
                          if (
                            /\d{6}/.test(card6) &&
                            !/^(506(?:099|1(?:[0-9][0-8]|[0-8]9))|6500(?:[0-2][2-7]|[01][89]|[12][01]))$/.test(card6) &&
                            !/^(5640)\d+$/.test(card6)
                          ) {
                            SAVED_CARDS_LIST.push(
                              withProperties(Object.create(__SavedCardProto__), { brand, hash, masked_pan })
                            );
                          }
                        }
                      });
    
                      Object.freeze(SAVED_CARDS_LIST) && (
                        objectWithAccessors.call(SAVED_CARDS, {
                          cards: () => SAVED_CARDS_LIST
                        }, false)
                      );
    
                      fetched !== true && (fetched = true);
                    }
    
                    return !!(fetched === true && Object.freeze(SAVED_CARDS));
                  })
              }
            })
          .catch(falseFunction)
        : void 0 )
    ))
  );
}

function removeSavedCard(card) {
  const $this = this;
  let savedCard = null;

  return Promise.resolve(
    checkSavedCardsEnabled.call(this, () => (
      (savedCard = selectSavedCardsFromList(SAVED_CARDS_LIST, card)) &&
      $fetcher(new URL('/v2/gpx/users/remove', process.env.API_BASE_URL), {
        body: JSON.stringify({
          public_key: getValueAtPath($this, 'PBFPubKey'),
          mobile_number: getValueAtPath(SAVED_CARDS, 'device.phone'),
          card_hash: getValueAtPath(savedCard, 'hash')
        })
      })
        .then(response => {
          if (isJsonResponse(response) && response.ok) {
            return response.json()
              .then(response => {
                const { message, status } = isPlainObject(response) ? response : {};

                if (status === 'success' && message === 'USER-REMOVED') {
                  SAVED_CARDS_LIST = removeFromArray(SAVED_CARDS_LIST, savedCard);
                  return !!Object.freeze(SAVED_CARDS_LIST);
                }

                return false;
              })
          }
        })
        .catch(falseFunction)
    ))
  );
}

function selectSavedCardsFromList(list, savedCard) {
  if (type(list) !== 'array') return null;

  if (list.indexOf(savedCard) < 0) {
    const isSavedCardObject = isPlainObject(savedCard);

    let { hash: cardHash, masked_pan: cardPan } =
      isSavedCardObject
        ? savedCard
        : (savedCard = String(savedCard), { hash: savedCard, masked_pan: savedCard });

    cardHash = cardHash != undefined && (cardHash = RegExp(`^${cardHash.replace(/\*/g, '\\*')}`, 'i'));
    cardPan = cardPan != undefined && (cardPan = RegExp(`^${cardPan.replace(/\*/g, '\\*')}`, 'i'));

    savedCard = list.find(item => {
      if (isPlainObject(item)) {
        if (String(item).replace(/\s/g, '') === String(savedCard).replace(/\s/g, '')) {
          return true;
        }

        const { hash, masked_pan } = item;

        let matches = (cardHash || cardPan) ? 1 : 0;

        if (isSavedCardObject) {
          cardHash && (matches = matches & cardHash.test(hash));
          cardPan && (matches = matches & cardPan.test(masked_pan));
        } else {
          matches = matches & (
            !!cardHash && cardHash.test(hash) ||
            !!cardPan && cardPan.test(masked_pan)
          );
        }

        return matches;
      }
    });
  }

  return savedCard || null;
}

export default function withSavedCards($context, callback) {
  if (!(isPlainObject($context) && Object.isExtensible($context))) {
    throw new Error('Cannot setup input fields without appropriate context object.');
  }

  setTimeout(() => {
    const { DATA: data } = $context;
    const savedCards = getValueAtPath(data, 'saved_cards');
    const savedCardsEnabled = checkSavedCardsEnabled.call(data);

    let { device, cards } = isPlainObject(savedCards) ? savedCards : {};

    const $reactiveCardsContext = withReactiveFields(Object.create(null), 'CARDS');

    const $savedCards = withProperties(Object.create(null), {
      enabled: savedCardsEnabled,

      fetch: (function () {
        if (savedCardsEnabled) {
          let fetched = false;
          const fetcher = fetchSavedCards.bind(data);

          return function __fetchSavedCards__() {
            if (!fetched) {
              fetcher().then(() => {
                const savedCards = getValueAtPath(data, 'saved_cards');
                ({ device, cards } = isPlainObject(savedCards) ? savedCards : {});
                $reactiveCardsContext.CARDS = cards;
                fetched = true;
              });
            }
          }
        }

        return falseFunction;
      })(),

      remove: (function () {
        if (savedCardsEnabled) {
          const remover = removeSavedCard.bind(data);

          return function __removerSavedCards__(savedCard) {
            if (getValueAtPath(cards, 'length') > 0) {
              remover(savedCard).then(() => {
                const savedCards = getValueAtPath(data, 'saved_cards');
                ({ cards } = isPlainObject(savedCards) ? savedCards : {});
                $reactiveCardsContext.CARDS = cards;
              });
            }
          }
        }

        return falseFunction;
      })()
    });

    objectWithAccessors.call($savedCards, {
      cards: () => cards,
      device: () => device
    }, false);

    $reactiveCardsContext.CARDS = cards;

    Object.defineProperties($savedCards, {
      $subscribe: {
        value(...args) { return $reactiveCardsContext.$subscribe('CARDS', ...args) }
      },

      $unsubscribe: {
        value(...args) { return $reactiveCardsContext.$unsubscribe('CARDS', ...args) }
      }
    });

    withProperties($context, { $savedCards });

    isFunction(callback) && callback.call($context);
  }, 0);

  return $context;
}

export {
  SAVED_CARDS,
  fetchSavedCards,
  removeSavedCard,
  selectSavedCardsFromList
}
